import { useState, useEffect, useCallback } from "react";
import { Button, ProctorReadiness } from "components";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { getCategories, getExam } from "./redux";
import { getIsPayed, getProctoringDisable, getProctoringPassed } from "selectors/userInfo";
import { useHistory } from "react-router";
import { EXAM } from "constants/paths";
import { getExamStart } from "../../api/PddTicketAPI";
import { useProctoring } from "hooks";
import { openModal } from "layout";
import { openPaymentPage } from "../../api/BillingAPI";

const DESKTOP_MIN_WIDTH = 910;

const StartExam = () => {
	const [loading, setLoading] = useState(false);
	const [showProctoring, setShowProctoring] = useState(false);
	const [isDesktop, setIsDesktop] = useState(
		window.innerWidth >= DESKTOP_MIN_WIDTH
	);
	
	const history = useHistory();
	const exam = useSelector(getExam);
	const dispatch = useDispatch();
	const categories = useSelector(getCategories);
	const proctoringDisable = useSelector(getProctoringDisable);
	const proctoringPassed = useSelector(getProctoringPassed);
	const isPayed = useSelector(getIsPayed);
	const [active, setActive] = useState({ name: "AB", id: 1 });

	const [attemptsLoading, setAttemptsLoading] = useState(false);
	const [attemptsChecked, setAttemptsChecked] = useState(false);

	const isProctoringAvailable = isPayed && !proctoringDisable;
	
	const {
		status: proctorStatus,
		attempts,
		error: proctorError,
		isBlocked,
		initProctoring,
		startSession,
		loadAttempts,
	} = useProctoring();

	// Отслеживание ширины экрана
	useEffect(() => {
		const handleResize = () => {
			setIsDesktop(window.innerWidth >= DESKTOP_MIN_WIDTH);
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		if (categories.length > 0) {
			setActive(categories[0]);
		}
	}, [categories]);

	// Обычный старт экзамена (без прокторинга)
	const handleStartExam = useCallback(() => {
		setLoading(true);
		dispatch(getExamStart(active.id));
	}, [dispatch, active]);

	// Старт с прокторингом
	const handleStartWithProctoring = useCallback(async () => {
		console.log("[StartExam] Starting with proctoring...");
		
		const initialized = await initProctoring();
		console.log("[StartExam] Proctoring initialized:", initialized);
		
		if (initialized) {
			const sessionId = await startSession();
			// Читаем identifier напрямую из глобальной переменной,
			// т.к. React state может не успеть обновиться
			const identifier = window.__proctoringIdentifier;
			console.log("[StartExam] Session started, ID:", sessionId);
			console.log("[StartExam] Proctoring identifier:", identifier);
			
			if (sessionId) {
				// Сохраняем sessionId и identifier в localStorage для передачи в ExamMode
				localStorage.setItem("proctoring_session_id", sessionId);
				if (identifier) {
					localStorage.setItem("proctoring_identifier", identifier);
				}
				console.log("[StartExam] Data saved to localStorage");
				setLoading(true);
				dispatch(getExamStart(active.id));
			} else {
				console.error("[StartExam] Failed to get session ID");
			}
		} else {
			console.error("[StartExam] Proctoring initialization failed");
		}
	}, [initProctoring, startSession, dispatch, active]);

	// Открытие модального окна подтверждения перед стартом прокторинга
	const handleReadyClick = useCallback(() => {
		dispatch(
			openModal({
				name: "ProctorConfirmStartModal",
				onConfirm: handleStartWithProctoring,
				remainingAttempts: attempts?.remaining_attempts ?? 0,
				totalAttempts: attempts?.total_attempts ?? 0,
			})
		);
	}, [dispatch, handleStartWithProctoring, attempts]);

	// Открытие модального окна при исчерпании попыток
	const openExhaustedModal = useCallback((usedAttempts?: number) => {
		dispatch(
			openModal({
				name: "ProctorAttemptsExhaustedModal",
				usedAttempts,
			})
		);
	}, [dispatch]);

	// Обработка клика на "Сдать с прокторингом"
	const handleProctoringClick = useCallback(async () => {
		setAttemptsLoading(true);
		setAttemptsChecked(true);
		await loadAttempts();
		setAttemptsLoading(false);
	}, [loadAttempts]);

	// После загрузки попыток решаем что делать
	useEffect(() => {
		if (attemptsChecked && attempts && !attemptsLoading) {
			setAttemptsChecked(false);
			if (isBlocked) {
				openExhaustedModal(attempts.used_attempts);
			} else {
				setShowProctoring(true);
			}
		}
	}, [attempts, isBlocked, attemptsLoading, attemptsChecked, openExhaustedModal]);

	// Переход к экзамену после загрузки
	useEffect(() => {
		if (exam) {
			setLoading(false);
			history.push(`${EXAM}?type=exam&category=${active.id}`);
		}
	}, [exam, history, active.id]);

	const handlePayment = useCallback(async () => {
		await openPaymentPage("web");
	}, []);

	// Показываем блок подготовки к прокторингу
	if (showProctoring && isDesktop && isProctoringAvailable) {
		return (
			<StarterWrapper>
				<BackButton onClick={() => setShowProctoring(false)}>
					← Назад
				</BackButton>
				<ProctorReadiness
					status={proctorStatus}
					attempts={attempts}
					error={proctorError}
					onStartProctoring={handleReadyClick}
					isLoading={loading || proctorStatus === "initializing"}
				/>
			</StarterWrapper>
		);
	}

	return (
		<StarterWrapper>
			<StarterTitle>Экзамен по ПДД {new Date().getFullYear()}</StarterTitle>
			<StarterDescription>
				Экзаменационный билет состоит из 20 вопросов. У Вас будет всего 20 минут
				что бы ответить на все вопросы. Максимальное количество ошибок - 2, за
				каждую добавляются по 5 вопросов, ответив на которые, ошибки считаются
				исправленными.
			</StarterDescription>
			<Head>
				<Content>
					{categories.map((item) => (
						<ButtonSwitch
							onClick={() => setActive(item)}
							active={item.name === active?.name}
							isLeft={
								item.name === active.name && active?.name === "CD" ? 1 : 0
							}
							isRight={
								item.name === active.name && active?.name === "AB" ? 1 : 0
							}
							key={item.id}
						>
							{item.name}
						</ButtonSwitch>
					))}
				</Content>
			</Head>
			
			<ButtonsContainer>
				{isProctoringAvailable ? (
					<>
						{isDesktop && !proctoringPassed && (
							<ProctorButton
								buttonTheme="primary"
								onClick={handleProctoringClick}
								disabled={loading || attemptsLoading}
							>
								{attemptsLoading ? "Загрузка..." : "Сдать с прокторингом"}
							</ProctorButton>
						)}
						<StartButton
							disabled={loading}
							onClick={handleStartExam}
							buttonTheme={isDesktop ? "secondary" : "primary"}
						>
							{isDesktop ? "Тренировка без прокторинга" : "Начать"}
						</StartButton>
					</>
				) : (
					<>
						<MarketingText>
							Проверь себя перед экзаменом в ГАИ для наилучшего результата.<br/>
							Сдай экзамен с прокторингом (с учётом контроля процедуры сдачи).<br/>
							Это максимально повысит вероятность сдать экзамен в ГАИ с 1-го раза.
						</MarketingText>
						<PaymentButton
							buttonTheme="primary"
							onClick={handlePayment}
						>
							Оплатить
						</PaymentButton>
						<StartButton
							disabled={loading}
							onClick={handleStartExam}
							buttonTheme="secondary"
						>
							Тренировка без прокторинга
						</StartButton>
					</>
				)}
			</ButtonsContainer>

			{isProctoringAvailable && isDesktop && !proctoringPassed && (
				<HintText>
					Для официальной сдачи экзамена выберите режим с прокторингом
				</HintText>
			)}
		</StarterWrapper>
	);
};

export default StartExam;

const StarterWrapper = styled.div`
	margin: 100px auto;
	padding: 0 20px;

	@media screen and (max-width: 910px) {
		margin: 15px auto;
	}
`;

const StarterTitle = styled.p`
	text-align: center;
	font-size: 1.875em;
	font-weight: 600;
	margin: 0 0 24px;
`;

const StarterDescription = styled.p`
	text-align: center;
	max-width: 510px;
	margin: auto auto 40px;
`;

const ButtonsContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
	max-width: 320px;
	margin: 0 auto;
`;

const StartButton = styled(Button)`
	display: block;
	width: 100%;
	height: 40px;
`;

const ProctorButton = styled(Button)`
	display: block;
	width: 100%;
	height: 48px;
	font-weight: 600;
`;

const BackButton = styled.button`
	background: none;
	border: none;
	color: #007bff;
	cursor: pointer;
	font-size: 1rem;
	padding: 8px 0;
	margin-bottom: 16px;

	&:hover {
		text-decoration: underline;
	}
`;

const HintText = styled.p`
	text-align: center;
	font-size: 0.85rem;
	color: #999;
	margin-top: 16px;
`;

const MarketingText = styled.p`
	text-align: center;
	font-size: 0.95rem;
	color: #555;
	line-height: 1.5;
	margin-bottom: 8px;
`;

const PaymentButton = styled(Button)`
	display: block;
	width: 100%;
	height: 48px;
	font-weight: 600;
`;

const ButtonSwitch = styled.button<{
	active: boolean;
	isRight: number;
	isLeft: number;
}>`
	background: ${({ active }) => (active ? "#fff" : "#F7F8FA")};
	padding: 5px 75px;
	border-radius: 8px;
	width: 250px;
	border: 1px solid #dadde4;
	border-left-width: ${({ isLeft }) => isLeft};
	border-right-width: ${({ isRight }) => isRight};
	border-top-width: 0;
	border-bottom-width: 0;
	cursor: pointer;
`;

const Head = styled.div`
	display: flex;
	margin-bottom: 15px;
	justify-content: center;
`;

const Content = styled.div`
	background: #f7f8fa;
	border-radius: 8px;
	border: 1px solid #dadde4;
`;
